import React, { useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { Share2, Download, Loader2 } from 'lucide-react';

/**
 * Renders its children into an offscreen wrapper, then snapshots it.
 *
 * Usage:
 *   <ShareAsImageButton filename="booking-123" title="My Skytone booking">
 *     <MyPrettyCard data={...} />
 *   </ShareAsImageButton>
 *
 * Renders a small "Share" button. On tap:
 *   - Mobile with Web Share API + files → native share sheet
 *   - Otherwise → downloads PNG
 */
const ShareAsImageButton = ({
  children,
  filename = `skytone-${Date.now()}`,
  title = 'Skytone',
  text = 'Check this out on Skytone',
  className = '',
  label = 'Share',
  iconOnly = false,
  // Card is rendered at this pixel width, then scaled 2x for retina output
  cardWidth = 720,
}) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2200);
  };

  const handleShare = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      // Wait for images inside the card to load
      await waitForImages(cardRef.current);

      const blob = await toBlob(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
        // Skip the hidden container wrapper — snapshot only the card
        width: cardWidth,
      });

      if (!blob) throw new Error('Failed to render image');

      const file = new File([blob], `${filename}.png`, { type: 'image/png' });

      // Try native share first (mobile)
      if (
        typeof navigator !== 'undefined' &&
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({ files: [file], title, text });
        showToast('Shared!');
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        showToast('Image downloaded');
      }
    } catch (err) {
      // User cancelled share sheet → not a real error
      if (err?.name === 'AbortError') {
        // silent
      } else {
        console.error('Share failed:', err);
        showToast('Could not share image', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        disabled={busy}
        className={`inline-flex items-center justify-center gap-2 font-bold transition-colors disabled:opacity-60 ${className}`}
        title="Share as image"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
        {!iconOnly && <span>{busy ? 'Preparing…' : label}</span>}
      </button>

      {/* Hidden render target — offscreen so it never affects layout */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          width: `${cardWidth}px`,
          pointerEvents: 'none',
          opacity: 0,
        }}
      >
        <div ref={cardRef} style={{ width: `${cardWidth}px` }}>
          {children}
        </div>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[120]">
          <div
            className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
              toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
            }`}
          >
            {toast.msg}
          </div>
        </div>
      )}
    </>
  );
};

// Helper: wait for all <img> children to be loaded (or fail) before snapshotting
const waitForImages = (node) => {
  const imgs = Array.from(node.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
          // Safety timeout so we never hang
          setTimeout(resolve, 3000);
        })
    )
  );
};

export default ShareAsImageButton;